import styled from 'styled-components';

/* ── Stepper layout wrappers ─────────────────────────────────────────── */

/**
 * Flex row that lays out the step nodes with connecting lines between them.
 * On mobile, wraps into a breadcrumb row with chevron separators.
 */
export const StepperWrapper = styled.nav`
  display: flex;
  align-items: center;
  flex: 1;
  overflow: visible;

  @media (max-width: 767px) {
    flex-wrap: wrap;
    width: 100%;
    gap: 0;
    overflow: visible;
    margin-bottom: 8px;
  }
`;

/**
 * Desktop-only wrapper for the stepper.
 * Applies asymmetric padding to isolate the progress bar from the cart area.
 * Hidden on mobile; MobileStepperWrapper handles mobile layout.
 */
export const DesktopStepperContainer = styled.div`
  display: flex;
  flex: 0 1 55%;
  max-width: 55%;
  padding: 0 20px 0 0;
  box-sizing: border-box;
  gap: 4px;

  @media (max-width: 767px) {
    display: none;
  }
`;

/**
 * Mobile-only breadcrumb wrapper.
 * Hidden on desktop to avoid duplication with DesktopStepperContainer.
 */
export const MobileStepperWrapper = styled.nav`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    gap: 0;
    overflow: visible;
    margin-bottom: 8px;
  }
`;

/* ── Step connector ──────────────────────────────────────────────────── */

/**
 * Horizontal line connecting two step nodes on desktop.
 * Replaced by a BreadcrumbChevron on mobile.
 * Always renders in gray; does not change color with step state.
 */
export const StepConnector = styled.div`
  flex: 1;
  height: 1px;
  min-width: 24px;
  margin: 0 12px;
  background: #D8DADA;
  transition: background 0.3s ease;

  @media (max-width: 767px) {
    display: none;
  }
`;

/* ── Individual step node ────────────────────────────────────────────── */

/**
 * Wrapper for a single step: lays out the circle and label side-by-side.
 *
 * @prop {boolean} isClickable - When true, renders pointer cursor and
 *   underlines the label on hover.
 */
export const StepNode = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};

  ${({ isClickable }) =>
    isClickable &&
    `
    &:hover .progress-step-label {
      text-decoration: underline;
    }
  `}

  @media (max-width: 767px) {
    gap: 6px;
    flex-shrink: 1;
    flex-grow: 0;
  }
`;

/**
 * Numbered circle for a step — desktop only (hidden on mobile).
 *
 * States:
 *   isCompleted – filled black, white number
 *   isActive    – filled Verizon red (#EE001E), white number, slight scale
 *   default     – outlined gray, gray number
 *
 * @prop {boolean} isCompleted
 * @prop {boolean} isActive
 * @prop {boolean} isClickable
 */
export const StepCircle = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Verizon-NHG-eTX', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  border: 1px solid
    ${({ isCompleted, isActive }) => {
      if (isCompleted) return '#000000';
      if (isActive) return '#EE001E';
      return '#D8DADA';
    }};
  background: ${({ isCompleted, isActive }) => {
    if (isCompleted) return '#000000';
    if (isActive) return '#EE001E';
    return 'transparent';
  }};
  color: ${({ isCompleted, isActive }) =>
    isCompleted || isActive ? '#ffffff' : '#716F6D'};
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  padding: 0;
  transition: background 0.3s ease, border-color 0.3s ease;
  transform: ${({ isActive }) => (isActive ? 'scale(1.1)' : 'scale(1)')};

  &:focus-visible {
    outline: 2px solid #EE001E;
    outline-offset: 3px;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

/**
 * Text label next to the step circle on desktop; the only visible element
 * on mobile (breadcrumb style).
 *
 * @prop {boolean} isActive     - Red (#EE001E), bold.
 * @prop {boolean} isCompleted  - Black, underlined (clickable indicator).
 */
export const StepLabel = styled.span`
  font-family: 'Verizon-NHG-eTX', sans-serif;
  font-size: 11px;
  font-weight: ${({ isActive }) => (isActive ? '700' : '400')};
  line-height: 14px;
  color: ${({ isActive, isCompleted }) => {
    if (isActive) return '#EE001E';
    if (isCompleted) return '#000000';
    return '#716F6D';
  }};
  text-decoration: ${({ isCompleted }) => (isCompleted ? 'underline' : 'none')};
  white-space: nowrap;
  cursor: inherit;
  transition: color 0.3s ease, text-decoration-color 0.3s ease;

  @media (max-width: 767px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

/* ── Mobile breadcrumb chevron ───────────────────────────────────────── */

/**
 * Thin black chevron separator between breadcrumb steps on mobile.
 * Replaces the StepConnector line; hidden on desktop.
 */
export const BreadcrumbChevron = styled.span`
  display: none;

  @media (max-width: 767px) {
    display: inline-flex;
    align-items: center;
    margin: 0 8px;
    color: #000000;
    font-size: 16px;
    line-height: 1;
  }
`;
